import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  listarFormatos, obtenerFormato, guardarFormato, actualizarFormato, borrarFormato,
  docxDeFormato, reemplazarDocxDeFormato, camposDelWord,
  estadoMotor, userIdDeCuenta,
  listarPerfilesDeCorreo, crearPerfilDeCorreo, editarPerfilDeCorreo,
  borrarPerfilDeCorreo, marcarPerfilPrincipal, probarPerfilDeCorreo,
  loQuePuedo, listarPermisos, darPermiso, quitarPermiso
} from '../../services/minutasV2Service';
import { personasDelDirectorio } from '../../services/planner';
import SelectorDePersona from './SelectorDePersona';
import { tokenRequest } from '../../authConfig';
import { FUENTE, useTema } from './tema';
import SelectorTema from './SelectorTema';
import Cuenta from './Cuenta';
import VistaWord from './VistaWord';
import { useConfirmacion } from './Confirmacion';

/**
 * Configuración — las plantillas de minuta y el aspecto de la app.
 *
 * Una plantilla es el .docx que usa la organización como acta: se sube una vez
 * aquí y luego se elige en cada reunión.
 *
 * En el código se llama «formato», y así se queda: es como lo nombran la API,
 * la base de datos y media aplicación. Renombrar todo eso para que coincida con
 * un rótulo sería cambiar el país por el mapa. Lo que la persona lee es
 * «plantilla» porque es lo que tiene delante.
 *
 * Aquí se ve y se toca: la plantilla se abre en Word dentro de la ventana, y lo
 * que se guarde ahí se guarda en ella. Antes, corregir una errata del encabezado
 * obligaba a bajar el .docx, abrirlo, arreglarlo y volver a subirlo como
 * plantilla nueva — con lo que quedaban dos, y nadie sabía cuál era la buena.
 *
 * Dos maneras de usarla, y conviene distinguirlas porque dan resultados
 * distintos:
 *
 *  - **Rellenar el Word original** — vuelve TU documento, con los datos de la
 *    reunión dentro y todo lo demás intacto. Funciona de dos maneras, y la
 *    persona no tiene que elegir: si el .docx trae campos marcados
 *    (`{{Campo}}`, `@Campo`, `[Campo]`) se rellenan esos; si no los trae —el
 *    caso normal, porque lo que la gente sube es el acta de la reunión
 *    anterior— los huecos se deducen de su estructura: pares "Fecha: …",
 *    tablas con cabecera, bloques de texto rotulados.
 *  - **Generar informe** — se redacta una minuta nueva siguiendo la estructura
 *    del formato. El resultado es HTML, no tu .docx.
 *
 * Por eso al subir una plantilla se dice qué se ha reconocido en ella: no para
 * pedir que la prepare, sino para que vea que su documento se ha entendido.
 */

/**
 * Longitud mínima del prompt.
 *
 * No es un número mágico con pretensiones: es el umbral por debajo del cual lo
 * escrito no llega a ser una instrucción ("formal", "bien") y el modelo acaba
 * decidiendo por su cuenta. Lo suficiente para obligar a una frase.
 */
const MINIMO_PROMPT = 40;

const EJEMPLO_PROMPT = `Redacta el acta en español neutro y en tercera persona, con tono formal.

En "Temas tratados", una línea por asunto, sin repetir lo dicho literalmente.
En "Acuerdos", solo compromisos firmes, con su responsable y la fecha si se dijo.
No incluyas conversación intrascendente ni saludos.
Si un dato no se mencionó en la reunión, escribe "No especificado"; nunca lo deduzcas.`;

const clavePreferido = (userId) => `minutas-v2-formato:${userId || 'anonimo'}`;

/**
 * "(4 datos, 3 tablas y 2 bloques de texto)" — para que quien sube su acta
 * reconozca su propio documento en lo que se le describe, en vez de leer un
 * número suelto.
 */
function resumenRegiones(regiones) {
  const n = (tipo) => regiones.filter(r => r.tipo === tipo).length;
  const partes = [];
  if (n('valor')) partes.push(`${n('valor')} ${n('valor') === 1 ? 'dato' : 'datos'}`);
  if (n('lista')) partes.push(`${n('lista')} ${n('lista') === 1 ? 'tabla' : 'tablas'}`);
  if (n('bloque')) partes.push(`${n('bloque')} ${n('bloque') === 1 ? 'bloque de texto' : 'bloques de texto'}`);
  if (partes.length === 0) return '';
  const ultimo = partes.pop();
  return ` (${partes.length ? partes.join(', ') + ' y ' + ultimo : ultimo})`;
}

/** El formato que se propone por defecto al abrir una reunión. */
export function formatoPreferido(userId) {
  try {
    const valor = window.localStorage.getItem(clavePreferido(userId));
    return valor || null;
  } catch {
    return null;
  }
}

export function guardarFormatoPreferido(userId, formatId) {
  try {
    if (formatId) window.localStorage.setItem(clavePreferido(userId), String(formatId));
    else window.localStorage.removeItem(clavePreferido(userId));
  } catch {
    /* sin almacenamiento local, la elección dura lo que la sesión */
  }
}

/**
 * Las secciones, en el orden en que se necesitan.
 *
 * «Formatos» va primera porque es a lo que se entra: las otras tres se tocan
 * una vez y no se vuelven a mirar. «Estado del sistema» solo aparece cuando hay
 * algo que contar del servidor.
 */
const APARTADOS = [
  // El id sigue siendo 'formatos' y el rótulo es «Plantillas» a propósito: la
  // API, la base de datos y media aplicación llaman formato a esto, y renombrar
  // todo eso por un rótulo sería cambiar el país por el mapa. Lo que la persona
  // lee es lo que tiene delante — un .docx que sirve de plantilla.
  { id: 'formatos', etiqueta: 'Plantillas', icono: '📄' },
  // Solo lo ve quien administra. No por secretismo: para quien no reparte
  // permisos es una pantalla que no puede usar, y una opción que lleva a «no
  // puedes» es peor que no estar.
  { id: 'permisos', etiqueta: 'Permisos', icono: '🔑', soloAdmin: true },
  { id: 'correo', etiqueta: 'Correo', icono: '✉' },
  { id: 'apariencia', etiqueta: 'Apariencia', icono: '◐' },
  { id: 'sesion', etiqueta: 'Sesión', icono: '👤' },
  { id: 'estado', etiqueta: 'Estado del sistema', icono: '◇' }
];

const estilos = (C) => ({
  // ── La configuración es un alto en el camino, no un destino ─────────────
  //
  // Era una página entera: se salía de la reunión, se cambiaba una cosa y había
  // que volver a entrar y esperar a que cargara otra vez. Como ventana encima,
  // lo de detrás sigue ahí —se ve, y se vuelve cerrando— y el cambio no cuesta
  // perder el sitio.
  //
  // Y con las cuatro secciones una debajo de otra, «Apariencia» quedaba a dos
  // pantallas de scroll por debajo de la lista de formatos. Un menú a la
  // izquierda las pone todas a un clic y a la vista, que es de lo que sirve un
  // menú: enseñar lo que hay, no solo llevar a ello.
  velo: {
    position: 'fixed', inset: 0, zIndex: 60,
    background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: FUENTE
  },
  modal: {
    width: '100%', maxWidth: 1040, height: 'min(84vh, 760px)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    background: C.fondo, color: C.texto,
    border: `1px solid ${C.borde}`, borderRadius: 14,
    boxShadow: '0 24px 70px rgba(0,0,0,.5)'
  },
  cabecera: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '16px 18px 16px 22px', borderBottom: `1px solid ${C.borde}`
  },
  cerrar: {
    marginLeft: 'auto', width: 30, height: 30, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: `1px solid ${C.borde}`,
    color: C.suave, cursor: 'pointer', fontSize: 16, lineHeight: 1, fontFamily: FUENTE
  },
  cuerpo: { display: 'flex', flex: 1, minHeight: 0 },
  menu: {
    flex: '0 0 214px', borderRight: `1px solid ${C.borde}`, background: C.fondoHondo,
    padding: 10, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto'
  },
  opcion: (activa) => ({
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '9px 11px', borderRadius: 8, border: 'none', cursor: 'pointer',
    textAlign: 'left', fontSize: 13.5, fontFamily: FUENTE,
    background: activa ? C.panelAlto : 'transparent',
    color: activa ? C.texto : C.suave,
    fontWeight: activa ? 600 : 400
  }),
  panelDerecho: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '22px 26px 30px' },

  h1: { fontSize: 19, fontWeight: 600, margin: 0 },
  // Sin tarjeta: dentro del panel ya no hay nada más con lo que confundirla, y
  // un recuadro dentro de otro recuadro solo estrecha el contenido.
  seccion: { marginBottom: 4 },
  h2: { fontSize: 16.5, fontWeight: 600, margin: '0 0 6px' },
  ayuda: { fontSize: 13.5, color: C.suave, lineHeight: 1.65, margin: '0 0 18px' },

  zona: (activa) => ({
    border: `2px dashed ${activa ? C.acento : C.borde}`,
    background: activa ? C.acentoSuave : C.panelAlto,
    borderRadius: 10, padding: 28, textAlign: 'center', cursor: 'pointer',
    transition: 'all .15s', color: C.suave, fontSize: 14
  }),

  fila: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '13px 15px',
    border: `1px solid ${C.borde}`, borderRadius: 10, marginBottom: 10,
    background: C.panelAlto
  },
  // La marca de «esta es la que se usa». Es un botón de radio y no un botón que
  // dice «Usar por defecto» porque son cosas distintas: un botón es una acción
  // que se pulsa y desaparece; esto es un estado que hay que poder LEER de un
  // vistazo en una lista, y saber cuál está marcada sin compararla con las
  // demás. Con cuatro plantillas, buscar cuál no lleva el botón era el trabajo.
  marca: {
    display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
    fontSize: 12.5, color: C.tenue, whiteSpace: 'nowrap', userSelect: 'none'
  },
  radio: { width: 16, height: 16, accentColor: C.acento, cursor: 'pointer', margin: 0 },

  nombre: { fontSize: 14.5, fontWeight: 600, margin: 0 },
  detalle: { fontSize: 12.5, color: C.tenue, marginTop: 3 },

  boton: {
    padding: '9px 16px', borderRadius: 8, border: `1px solid ${C.borde}`,
    background: C.panel, color: C.texto, fontSize: 13.5, fontFamily: FUENTE, cursor: 'pointer'
  },
  botonAcento: { background: C.acento, borderColor: C.acento, color: C.acentoTexto },
  botonPeligro: { color: C.error, borderColor: C.error },

  campo: {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 14, fontFamily: FUENTE, marginBottom: 10
  },
  area: {
    width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 8,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 14, fontFamily: FUENTE, lineHeight: 1.65, minHeight: 190,
    resize: 'vertical', marginBottom: 6
  },
  contador: { fontSize: 12, color: C.tenue, textAlign: 'right', marginBottom: 12 },
  etiqueta: { fontSize: 13, color: C.suave, display: 'block', marginBottom: 5 },
  tarjetaCorreo: {
    border: `1px solid ${C.borde}`, borderRadius: 9, padding: '13px 15px',
    marginBottom: 10, background: C.panel
  },
  selloBuzon: {
    fontSize: 11, padding: '2px 7px', borderRadius: 20,
    background: C.acento, color: C.acentoTexto
  },
  rejillaCorreo: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 11
  },

  insignia: (color) => ({
    display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11.5,
    background: `${color}22`, color, border: `1px solid ${color}55`, marginLeft: 8
  })
});

const Configuracion = ({ onVolver }) => {
  const { C, aviso } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const { instance, accounts } = useMsal();
  const cuenta = accounts && accounts[0];
  // La dirección de quien ha entrado. `username` es el UPN en Microsoft Entra,
  // que salvo excepciones es su correo; `userId` NO sirve para esto.
  const miCorreo = (cuenta && (cuenta.username || (cuenta.idTokenClaims
    && (cuenta.idTokenClaims.preferred_username || cuenta.idTokenClaims.email)))) || '';
  const userId = userIdDeCuenta(cuenta);

  // ── Lo que esta persona puede hacer ──────────────────────────────────
  //
  // Lo dice el servidor a partir del token, no el navegador. Se usa para
  // ESCONDER lo que no toca; impedirlo es cosa del servidor, que lo vuelve a
  // comprobar en cada escritura. Esconder un botón sin cerrar la puerta sería
  // pintar la puerta del color de la pared.
  //
  // Mientras no se sabe, `null`: así la pantalla no enseña «no puedes» durante
  // el segundo que tarda en contestar, que es la peor manera de recibir a
  // alguien que sí puede.
  const [puedo, setPuedo] = useState(null);

  useEffect(() => {
    let vigente = true;
    loQuePuedo()
      .then(p => { if (vigente) setPuedo(p); })
      .catch(() => {
        // Sin respuesta se da por supuesto que NO. Al revés —dar por supuesto
        // que sí— enseñaría el botón de subir plantillas a todo el mundo cada
        // vez que la red fallara, y el error llegaría al pulsarlo.
        if (vigente) setPuedo({ puedePlantillas: false, administra: false, permiso: null });
      });
    return () => { vigente = false; };
  }, []);

  // ── La lista de quien puede ──────────────────────────────────────────
  //
  // `null` mientras no se sabe, para no enseñar «no hay nadie» durante el
  // segundo que tarda en llegar: eso asusta justo a quien acaba de entrar a
  // comprobar que su gente sigue teniendo permiso.
  const [permisos, setPermisos] = useState(null);
  const [directorio, setDirectorio] = useState([]);
  const [avisoPermisos, setAvisoPermisos] = useState(null);
  // Elegidas y todavía sin guardar. Se pide poder añadir a varias de una vez,
  // y es lo razonable: dar permiso a un equipo de cinco no son cinco visitas a
  // esta pantalla.
  const [porAñadir, setPorAñadir] = useState([]);
  const [dando, setDando] = useState(false);
  // Si la lista no se pudo leer. Distinto de que esté vacía, y hay que
  // distinguirlo: «todavía no hay nadie» sobre una lista que no se pudo leer es
  // una mentira tranquilizadora — y es lo que se vio la primera vez que esto
  // falló, mientras en rojo encima ponía que no había permiso.
  const [falloAlLeer, setFalloAlLeer] = useState(false);

  const refrescarPermisos = useCallback(async () => {
    try {
      setPermisos(await listarPermisos());
      setFalloAlLeer(false);
    } catch (e) {
      setPermisos([]);
      setFalloAlLeer(true);
      setAvisoPermisos({ malo: true, texto: e.mensajeUsuario || e.message });
    }
  }, []);

  /**
   * El identificador con el que esta persona existe en el resto de la base.
   *
   * «oid.tid». El directorio da el oid a secas, así que el inquilino se le pega
   * aquí. Sin esto el permiso quedaría a nombre de una identidad que no existe
   * y la persona seguiría sin poder subir nada, sin saber por qué.
   */
  const identidadDe = useCallback(
    (persona) => `${persona.id}.${(cuenta && cuenta.tenantId) || ''}`,
    [cuenta]
  );

  /** Se apunta a la lista de elegidas. No se guarda hasta pulsar. */
  const elegirParaPermiso = useCallback((persona) => {
    if (!persona || !persona.id) return;
    setAvisoPermisos(null);
    setPorAñadir(actuales => (
      actuales.some(p => p.id === persona.id) ? actuales : [...actuales, persona]
    ));
  }, []);

  const quitarDeElegidas = useCallback((id) => {
    setPorAñadir(actuales => actuales.filter(p => p.id !== id));
  }, []);

  /** Da el permiso a todas las elegidas, y cuenta qué pasó con cada una. */
  const guardarElegidas = useCallback(async () => {
    if (porAñadir.length === 0) return;
    setDando(true);
    setAvisoPermisos(null);
    const bien = [];
    const mal = [];
    for (const persona of porAñadir) {
      try {
        await darPermiso({
          userId: identidadDe(persona),
          correo: persona.correo,
          nombre: persona.nombre,
          permiso: 'plantillas'
        });
        bien.push(persona.nombre || persona.correo);
      } catch (e) {
        // Una que falle no puede llevarse por delante a las demás: se sigue, y
        // al final se dice cuáles entraron y cuáles no. Cortar en la primera
        // dejaría la mitad dentro sin decir cuál mitad.
        mal.push(`${persona.nombre || persona.correo} (${e.mensajeUsuario || e.message})`);
      }
    }
    setDando(false);
    setPorAñadir([]);
    setAvisoPermisos(
      mal.length === 0
        ? { texto: bien.length === 1
          ? `${bien[0]} ya puede subir plantillas.`
          : `${bien.length} personas ya pueden subir plantillas.` }
        : { malo: true, texto: `No se pudo con: ${mal.join('; ')}.`
          + (bien.length ? ` Sí entraron: ${bien.join(', ')}.` : '') }
    );
    refrescarPermisos();
  }, [porAñadir, identidadDe, refrescarPermisos]);

  const cambiarPermiso = useCallback(async (quien, nuevo) => {
    if (nuevo === quien.permiso) return;
    setAvisoPermisos(null);
    try {
      await darPermiso({
        userId: quien.userId, correo: quien.correo, nombre: quien.nombre, permiso: nuevo
      });
      refrescarPermisos();
      // Bajarse uno mismo de administrador es legitimo, y entonces esta
      // pantalla deja de ser suya. Se vuelve a preguntar que se puede en vez de
      // dejarla pintada contestando 403 a cada cosa, que es lo que pasaba.
      loQuePuedo().then(setPuedo).catch(() => {});
    } catch (e) {
      setAvisoPermisos({ malo: true, texto: e.mensajeUsuario || e.message });
    }
  }, [refrescarPermisos]);

  const sacarDePermisos = useCallback(async (quien) => {
    setAvisoPermisos(null);
    try {
      await quitarPermiso(quien.userId);
      setAvisoPermisos({ texto: `${quien.nombre || quien.correo} ya no puede subir plantillas.` });
      refrescarPermisos();
      // Quitarse a uno mismo es legitimo —se deja de administrar— y entonces
      // esta pantalla deja de existir. Se vuelve a preguntar que se puede en
      // vez de dejarla ahi enseñando una lista que ya no se puede tocar.
      loQuePuedo().then(setPuedo).catch(() => {});
    } catch (e) {
      setAvisoPermisos({ malo: true, texto: e.mensajeUsuario || e.message });
    }
  }, [refrescarPermisos]);

  // ── El buzon desde el que salen los correos ──────────────────────────
  //
  // `perfiles` es lo que la pantalla puede ver: nunca la contrasena, solo
  // `tienePassword`. `borrador` es el formulario que se esta escribiendo.
  //
  // La contrasena se manda SOLO si se escribio una nueva. Si se mandara
  // siempre, al editar el puerto se enviaria la cadena vacia que tiene la
  // pantalla —porque nunca se la damos— y se borraria la guardada: el correo
  // dejaria de salir y nadie relacionaria las dos cosas.
  const [perfiles, setPerfiles] = useState(null);
  const [hayClaveCorreo, setHayClaveCorreo] = useState(true);
  const [buzon, setBuzon] = useState(null);
  const [guardandoCorreo, setGuardandoCorreo] = useState(false);
  const [avisoCorreo, setAvisoCorreo] = useState(null);
  const [probando, setProbando] = useState(null);

  const NUEVO = useMemo(() => ({
    id: null, nombre: '', host: '', puerto: 587, usuario: '',
    desdeCorreo: '', desdeNombre: '', usaTLS: true, password: ''
  }), []);

  const refrescarCorreo = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await listarPerfilesDeCorreo(userId);
      setPerfiles(r.profiles || []);
      setHayClaveCorreo(r.hayClave !== false);
    } catch {
      setPerfiles([]);
    }
  }, [userId]);

  useEffect(() => { refrescarCorreo(); }, [refrescarCorreo]);

  const guardarCorreo = useCallback(async () => {
    if (!buzon || guardandoCorreo) return;
    setGuardandoCorreo(true);
    setAvisoCorreo(null);
    try {
      const datos = {
        nombre: buzon.nombre, host: buzon.host, puerto: Number(buzon.puerto) || 587,
        usuario: buzon.usuario, desdeCorreo: buzon.desdeCorreo,
        desdeNombre: buzon.desdeNombre, usaTLS: buzon.usaTLS
      };
      // Solo si se escribio algo. Ver arriba.
      if (buzon.password) datos.password = buzon.password;

      if (buzon.id) await editarPerfilDeCorreo(userId, buzon.id, datos);
      else await crearPerfilDeCorreo(userId, datos);

      await refrescarCorreo();
      setBuzon(null);
      setAvisoCorreo({ texto: 'Guardado.', malo: false });
    } catch (e) {
      setAvisoCorreo({ texto: e.message || 'No se pudo guardar.', malo: true });
    } finally {
      setGuardandoCorreo(false);
    }
  }, [buzon, guardandoCorreo, userId, refrescarCorreo]);

  const probarCorreo = useCallback(async (id) => {
    setProbando(id);
    setAvisoCorreo(null);
    try {
      // A DÓNDE se manda la prueba, dicho explícitamente.
      //
      // El servidor no lo puede deducir: el `userId` de esta aplicación es el
      // identificador de cuenta de Azure AD, no un correo. Dejar que el
      // servidor «usara el del actor» —como hace la implementación de la que
      // viene esto— hacía que la prueba fallara al instante por falta de
      // destinatario, y el mensaje culpaba al buzón.
      const r = await probarPerfilDeCorreo(userId, id, miCorreo);
      setAvisoCorreo(r.sent
        ? { texto: `Enviado a ${(r.to || []).join(', ')}. Revisa tu bandeja.`, malo: false }
        : { texto: r.error || 'No se pudo enviar.', malo: true });
    } catch (e) {
      setAvisoCorreo({ texto: e.message || 'No se pudo enviar.', malo: true });
    } finally {
      setProbando(null);
    }
  }, [userId, miCorreo]);

  const [apartado, setApartado] = useState('formatos');

  // Solo cuando se entra al apartado, y el directorio solo una vez: son
  // cientos de fichas y traerlas al abrir la configuracion seria pagarlas
  // aunque se venga a cambiar el tema.
  useEffect(() => {
    if (apartado !== 'permisos' || !puedo || !puedo.administra) return;
    refrescarPermisos();
    if (directorio.length > 0) return;
    (async () => {
      try {
        const token = (await instance.acquireTokenSilent({ ...tokenRequest, account: cuenta })).accessToken;
        setDirectorio(await personasDelDirectorio(token));
      } catch {
        // Sin directorio se puede seguir quitando permisos, que es lo urgente;
        // lo que no se puede es añadir buscando. Se dice al intentarlo.
        setDirectorio([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartado, puedo, refrescarPermisos]);

  const [formatos, setFormatos] = useState([]);
  const [preferido, setPreferido] = useState(() => formatoPreferido(userId));
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [nota, setNota] = useState(null);
  const [motor, setMotor] = useState(null);

  // El .docx en curso de subida, mientras se le pone nombre.
  const [pendiente, setPendiente] = useState(null);
  const [nombre, setNombre] = useState('');
  const [indicaciones, setIndicaciones] = useState('');
  const [esPublico, setEsPublico] = useState(false);
  // Si se está creando una. Es distinto de tener el documento elegido: primero
  // se abre el formulario y después se suelta el .docx dentro.
  const [creando, setCreando] = useState(false);
  const [seraPreferida, setSeraPreferida] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);

  const promptValido = indicaciones.trim().length >= MINIMO_PROMPT;

  // El formato cuyo prompt se está editando, y el texto en curso.
  const [editando, setEditando] = useState(null);
  const [promptEditado, setPromptEditado] = useState('');
  const [guardandoPrompt, setGuardandoPrompt] = useState(false);
  // El .docx de la plantilla que se está mirando. No viene en el listado —es
  // pesado y casi nunca hace falta—, se pide al abrirla.
  const [docxPlantilla, setDocxPlantilla] = useState(null);
  const [guardandoDocx, setGuardandoDocx] = useState(false);

  const inputArchivo = useRef(null);

  const tokenGraph = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ ...tokenRequest, account: cuenta });
    return r.accessToken;
  }, [instance, cuenta]);

  const recargar = useCallback(() => {
    if (!userId) return;
    setCargando(true);
    listarFormatos(userId)
      .then(f => { setFormatos(f); setError(null); })
      .catch(e => setError(e.mensajeUsuario || e.message))
      .finally(() => setCargando(false));
  }, [userId]);

  useEffect(recargar, [recargar]);

  useEffect(() => {
    estadoMotor().then(setMotor).catch(() => setMotor(null));
  }, []);

  // Escape cierra, como cualquier ventana. Sin esto, la única salida de algo
  // que tapa la pantalla entera es encontrar la × con el ratón.
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onVolver && onVolver(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onVolver]);

  // Aquí se preguntaba al servidor si quedaban plantillas de la versión
  // anterior sin importar, para ofrecer traerlas. Se ha ido con el botón: era
  // una pregunta en cada apertura de Configuración para una operación que se
  // hace una vez en la vida de una cuenta, y que ya se hizo.
  //
  // El servidor conserva las dos rutas. Si vuelve a hacer falta —una cuenta
  // nueva que venga de la v1— se trae el botón de vuelta, no el mecanismo.

  // ── Subir un formato ───────────────────────────────────────

  const abrirNueva = () => {
    setCreando(true);
    setPendiente(null);
    setNombre('');
    setIndicaciones('');
    setEsPublico(false);
    // La primera plantilla tiene que ser la preferida por fuerza —no hay otra—,
    // y la segunda casi siempre se sube para sustituir a la que había. Marcado
    // de entrada acierta casi siempre, y desmarcarlo cuesta un clic.
    setSeraPreferida(true);
    setError(null);
  };

  const cerrarNueva = () => {
    setCreando(false);
    setPendiente(null);
    setEsPublico(false);
  };

  const elegirArchivo = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setError('La plantilla tiene que ser un archivo .docx de Word.');
      return;
    }

    setError(null);

    // Solo se propone el nombre si no hay ninguno, y las indicaciones no se
    // tocan. En el formulario nuevo se puede elegir el documento DESPUÉS de
    // escribir el prompt, y se puede cambiar el documento con el prompt ya
    // escrito: borrarlo aquí haría perder un párrafo pensado por corregir el
    // archivo equivocado.
    if (!nombre.trim()) setNombre(file.name.replace(/\.docx$/i, ''));

    // Se detectan los campos antes de guardar: es lo que decide si este
    // formato podrá rellenarse conservando el Word o solo servirá de guion.
    try {
      const { campos, regiones } = await camposDelWord(file);
      setPendiente({ file, campos, regiones });
    } catch (e) {
      setPendiente({ file, campos: [], regiones: [], errorCampos: e.mensajeUsuario || e.message });
    }
  };

  const confirmarSubida = async () => {
    if (!pendiente) return;

    // El machote sin indicaciones deja al modelo adivinando el tono y el nivel
    // de detalle, y el resultado hay que reescribirlo. Por eso no es opcional.
    if (!promptValido) {
      setError(
        `Faltan las indicaciones para la IA: son las que le dicen cómo redactar el acta con ` +
        `este machote. Escribe al menos ${MINIMO_PROMPT} caracteres.`
      );
      return;
    }

    setSubiendo(true);
    setError(null);
    try {
      const guardado = await guardarFormato({
        file: pendiente.file,
        userId,
        nombre: nombre.trim() || pendiente.file.name,
        indicaciones: indicaciones.trim(),
        esPublico
      });
      setNota(`Plantilla "${guardado.name}" guardada.`);
      cerrarNueva();
      recargar();
      // Lo que se marcó en el formulario. Antes esto era «si no hay ninguna
      // preferida, que sea esta», que acertaba con la primera y no dejaba
      // decidirlo con las demás: había que guardarla y luego buscarla en la
      // lista para marcarla.
      if (seraPreferida || !preferido) elegirPreferido(guardado.formatId);
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setSubiendo(false);
    }
  };

  const elegirPreferido = (formatId) => {
    setPreferido(String(formatId));
    guardarFormatoPreferido(userId, formatId);
    setNota('Esta plantilla se propondrá en cada reunión.');
  };

  const borrar = async (formato) => {
    if (!await confirmar({
      titulo: 'Borrar esta plantilla',
      mensaje: `Se borrará «${formato.name}» y el .docx que subiste con él.\n\n` +
        'Las minutas ya generadas con esta plantilla se conservan, pero no se podrán volver a ' +
        'generar ni corregir sobre él sin subirlo otra vez.',
      aceptar: 'Borrarlo',
      peligro: true
    })) return;
    try {
      await borrarFormato(formato.formatId, userId);
      if (String(preferido) === String(formato.formatId)) {
        setPreferido(null);
        guardarFormatoPreferido(userId, null);
      }
      setNota(`Formato "${formato.name}" borrado.`);
      recargar();
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    }
  };

  const abrirEdicion = async (formato) => {
    setEditando(formato.formatId);
    setError(null);
    setDocxPlantilla(null);

    // El listado no trae las indicaciones (es liviano a propósito): se piden
    // para no presentar un campo vacío que borraría lo que ya había escrito.
    try {
      const completo = await obtenerFormato(formato.formatId, userId);
      setPromptEditado(completo.instructions || '');
    } catch (e) {
      setPromptEditado('');
      setError(e.mensajeUsuario || e.message);
    }

    // Y el documento, que es lo que se está describiendo con las indicaciones.
    // Va aparte y sin avisar si falla: si no se puede enseñar la plantilla,
    // sigue habiendo indicaciones que escribir, y un error rojo por no poder
    // enseñar algo que se mira de refilón estorbaría más de lo que ayuda.
    try {
      const { contentBase64 } = await docxDeFormato(formato.formatId, userId);
      setDocxPlantilla(contentBase64 || null);
    } catch {
      setDocxPlantilla(null);
    }
  };

  /**
   * Lo que se escribió en Word vuelve a la plantilla.
   *
   * El documento se edita en una copia —Word para la web abre archivos de una
   * unidad, y la plantilla vive en la base de datos—, así que sin esto lo
   * escrito se quedaría en esa copia y la plantilla seguiría igual. Dos
   * documentos con el mismo nombre y el que manda no es el que se ve: eso no se
   * descubre hasta que alguien genera un acta y le sale la versión vieja.
   *
   * El servidor vuelve a leer el documento entero al guardarlo. Tiene que
   * hacerlo: si se movieron los huecos o cambió una tabla, guardar el archivo
   * nuevo con la lectura del viejo dejaría una plantilla que rellena donde ya no
   * hay nada.
   */
  const guardarDocxPlantilla = async (formato, base64) => {
    setGuardandoDocx(true);
    setError(null);
    try {
      await reemplazarDocxDeFormato(formato.formatId, userId, {
        contentBase64: base64,
        fileName: formato.fileName
      });
      // Lo subido pasa a ser lo guardado: sin esto, el recuadro de Word creería
      // que la plantilla cambió por debajo y volvería a subirla.
      setDocxPlantilla(base64);
      setNota(`«${formato.name}» actualizada con lo que escribiste en Word.`);
      recargar();
    } catch (e) {
      setError(
        'Lo escribiste en Word, pero no se pudo guardar en la plantilla: ' +
        (e.mensajeUsuario || e.message)
      );
    } finally {
      setGuardandoDocx(false);
    }
  };

  const guardarPrompt = async () => {
    if (promptEditado.trim().length < MINIMO_PROMPT) {
      setError(`Las indicaciones deben tener al menos ${MINIMO_PROMPT} caracteres.`);
      return;
    }
    setGuardandoPrompt(true);
    setError(null);
    try {
      await actualizarFormato(editando, userId, { instructions: promptEditado.trim() });
      setNota('Indicaciones actualizadas. Se aplicarán en la próxima minuta que generes.');
      setEditando(null);
      recargar();
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setGuardandoPrompt(false);
    }
  };

  const cambiarVisibilidad = async (formato) => {
    try {
      await actualizarFormato(formato.formatId, userId, { isPublic: !formato.isPublic });
      recargar();
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    }
  };

  // Los avisos de lo que no se deshace se pintan dentro de la aplicación, no
  // con el cuadro del navegador.
  const [dialogo, confirmar] = useConfirmacion();

  // ── Vista ──────────────────────────────────────────────────

  return (
    <div style={S.velo} onMouseDown={onVolver}>
      {dialogo}

      {/* El clic se para aquí: pulsar dentro de la ventana no puede cerrarla,
          y pulsar fuera sí. `onMouseDown` y no `onClick` porque al arrastrar
          para seleccionar un texto se suelta el botón fuera, y con `click` eso
          cerraba la configuración en mitad de una selección. */}
      <div
        style={S.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Configuración"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header style={S.cabecera}>
          <h1 style={S.h1}>Configuración</h1>
          <button style={S.cerrar} onClick={onVolver} title="Cerrar" aria-label="Cerrar">×</button>
        </header>

        <div style={S.cuerpo}>
          <nav style={S.menu} aria-label="Secciones de la configuración">
            {APARTADOS
              .filter(a => a.id !== 'estado' || motor)
              .filter(a => !a.soloAdmin || (puedo && puedo.administra))
              .map(a => (
              <button
                key={a.id}
                style={S.opcion(apartado === a.id)}
                onClick={() => setApartado(a.id)}
                aria-current={apartado === a.id ? 'page' : undefined}
              >
                <span aria-hidden="true" style={{ width: 16, textAlign: 'center' }}>{a.icono}</span>
                {a.etiqueta}
              </button>
            ))}
          </nav>

          <div style={S.panelDerecho}>
        {error && <div style={aviso('error')}>{error}</div>}
        {nota && <div style={aviso('exito')}>{nota}</div>}

        {motor && !motor.ready && apartado === 'formatos' && (
          <div style={aviso('atencion')}>
            <strong>El motor de IA no está listo.</strong> {motor.mensaje} Puedes subir plantillas
            igualmente; generar minutas no funcionará hasta que se configure.
          </div>
        )}

        {/* ── Plantillas ── */}
        {apartado === 'formatos' && (
        <section style={S.seccion}>
          <h2 style={S.h2}>Plantillas</h2>

          {/* Aquí había un párrafo explicando qué es una plantilla, qué pasa si
              trae huecos marcados y qué pasa si no.

              Se ha ido porque llegaba antes de tiempo. Nadie entra a esta
              pantalla a aprender cómo funciona el relleno: entra a subir un
              .docx, o a cambiar cuál es la preferida. Y lo que el párrafo
              contaba se cuenta solo, y mejor, en el momento en que importa: al
              soltar el archivo se dice qué se ha reconocido en él —los campos
              marcados o las zonas deducidas— con los nombres de su propio
              documento delante. Eso sí se lee. */}

          {/* ── Crear una es un acto, no el estado de la pantalla ──────

              La zona de arrastre presidía esta pantalla, grande y punteada,
              aunque se entrara a otra cosa. Y subir una plantilla es raro: se
              hace una vez y se vuelve para cambiar cuál es la preferida o
              corregir un prompt. Lo que se hace siempre estaba debajo de un
              cartel para lo que se hace casi nunca.

              Ahora es un botón, y al pulsarlo se abre el formulario entero: el
              documento, cómo se llama, las indicaciones y si va a ser la
              preferida. Las cuatro decisiones de una plantilla nueva, juntas y
              antes de guardar — no repartidas entre un arrastre y una lista a
              la que había que volver después. */}
          {/* ── Subir plantillas lo lleva un grupo reducido ─────────────

              Una plantilla no es de quien la sube: lo que diga sale en las
              actas de toda la organizacion. Con todo el mundo pudiendo subir y
              compartir, el catalogo comun se llena de borradores y el formato
              oficial no tiene dueño.

              A quien no puede se le dice por que y a quien pedirlo, en vez de
              esconder el boton sin mas. Un boton que desaparece se lee como un
              fallo de la aplicacion, y quien lo usaba ayer vuelve mañana a ver
              si ha vuelto. */}
          {puedo && !puedo.puedePlantillas && (
            <div style={{ ...aviso('info'), marginBottom: 18 }}>
              Las plantillas son comunes a toda la organización, así que subirlas y
              compartirlas lo lleva un grupo reducido. Aquí puedes ver las que hay y
              elegir cuál usas por omisión. Si necesitas subir una, pídeselo a quien
              administre la aplicación.
            </div>
          )}

          {puedo && puedo.puedePlantillas && (!creando ? (
            <button
              style={{ ...S.boton, ...S.botonAcento, marginBottom: 18 }}
              onClick={abrirNueva}
            >
              ＋ Nueva plantilla
            </button>
          ) : (
            <div style={{ border: `1px solid ${C.borde}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Nueva plantilla</div>

              {/* El documento. Sigue siendo una zona de arrastre porque
                  arrastrar un .docx es la manera natural de darlo, pero ahora
                  vive dentro del formulario y no presidiendo la pantalla. */}
              <label style={S.etiqueta}>Documento de Word</label>
              {!pendiente ? (
                <div
                  style={{ ...S.zona(arrastrando), padding: 20, marginBottom: 4 }}
                  onClick={() => inputArchivo.current && inputArchivo.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
                  onDragLeave={() => setArrastrando(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setArrastrando(false);
                    elegirArchivo(e.dataTransfer.files && e.dataTransfer.files[0]);
                  }}
                >
                  <div style={{ fontSize: 22, marginBottom: 6, opacity: .6 }}>📄</div>
                  Arrastra aquí tu plantilla en Word, o pulsa para elegirla
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  border: `1px solid ${C.borde}`, borderRadius: 8,
                  padding: '10px 13px', marginBottom: 4
                }}>
                  <span aria-hidden="true">📄</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>{pendiente.file.name}</span>
                  <button
                    style={{ ...S.boton, padding: '5px 11px', fontSize: 12.5 }}
                    onClick={() => inputArchivo.current && inputArchivo.current.click()}
                  >
                    Cambiar
                  </button>
                </div>
              )}

              {pendiente && (pendiente.errorCampos ? (
                <div style={aviso('error')}>{pendiente.errorCampos}</div>
              ) : pendiente.campos.length > 0 ? (
                <div style={aviso('exito')}>
                  Se detectaron <strong>{pendiente.campos.length} campos marcados</strong>. Esta
                  plantilla se rellenará conservando el Word tal cual:{' '}
                  {pendiente.campos.slice(0, 6).map(c => c.token).join(', ')}
                  {pendiente.campos.length > 6 && ` y ${pendiente.campos.length - 6} más`}.
                </div>
              ) : pendiente.regiones && pendiente.regiones.length > 0 ? (
                // Un acta sin marcadores es el caso normal: la gente sube la
                // minuta de la reunión anterior. Se le enseña qué se ha
                // reconocido en su documento para que sepa qué va a cambiar.
                <div style={aviso('exito')}>
                  Se reconoció la estructura de tu acta:{' '}
                  <strong>{pendiente.regiones.length} zonas</strong> que se rellenarán con los datos
                  de cada reunión{resumenRegiones(pendiente.regiones)}. Se conserva todo lo demás
                  —logos, encabezados, tablas y estilos—; solo cambian los datos.
                  <div style={{ marginTop: 6, color: C.tenue }}>
                    {pendiente.regiones.slice(0, 7).map(r => r.etiqueta).filter(Boolean).join(' · ')}
                    {pendiente.regiones.length > 7 && ` y ${pendiente.regiones.length - 7} más`}
                  </div>
                </div>
              ) : (
                <div style={aviso('atencion')}>
                  No se reconoció ninguna zona rellenable: ni campos marcados, ni tablas, ni
                  etiquetas del tipo «Fecha:». Servirá como guion para redactar la minuta, pero no se
                  podrá rellenar este Word. Si es un acta con tablas, vuelve a guardarla desde Word
                  como .docx; si es un documento en blanco, marca los huecos con{' '}
                  <code>{'{{Nombre}}'}</code>, <code>@Nombre</code> o <code>[Nombre]</code>.
                </div>
              ))}

              <label style={S.etiqueta}>Nombre</label>
              <input style={S.campo} value={nombre} onChange={(e) => setNombre(e.target.value)} />

              <label style={S.etiqueta}>
                Indicaciones para la IA <span style={{ color: C.error }}>*</span>
              </label>
              <p style={{ ...S.ayuda, margin: '0 0 8px', fontSize: 12.5 }}>
                Explica cómo quieres que redacte el acta con este machote: tono, nivel de detalle,
                qué va en cada apartado y qué hacer cuando un dato no se mencionó. Cuanto más
                concreto, menos tendrás que corregir después.
              </p>
              <textarea
                style={{
                  ...S.area,
                  borderColor: indicaciones.trim().length > 0 && indicaciones.trim().length < MINIMO_PROMPT
                    ? C.atencion
                    : C.borde
                }}
                placeholder={EJEMPLO_PROMPT}
                value={indicaciones}
                onChange={(e) => setIndicaciones(e.target.value)}
              />
              <div style={S.contador}>
                {indicaciones.trim().length < MINIMO_PROMPT
                  ? `Escribe al menos ${MINIMO_PROMPT} caracteres (llevas ${indicaciones.trim().length}).`
                  : `${indicaciones.trim().length} caracteres`}
              </div>

              <label style={{ ...S.etiqueta, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={seraPreferida}
                  onChange={(e) => setSeraPreferida(e.target.checked)}
                />
                Usarla por defecto en cada reunión
              </label>

              <label style={{ ...S.etiqueta, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <input type="checkbox" checked={esPublico} onChange={(e) => setEsPublico(e.target.checked)} />
                Compartirla con toda la organización
              </label>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  style={{ ...S.boton, ...S.botonAcento, opacity: pendiente && promptValido ? 1 : .5 }}
                  onClick={confirmarSubida}
                  disabled={subiendo || !pendiente || !promptValido}
                  title={
                    !pendiente
                      ? 'Falta el documento de Word'
                      : promptValido ? '' : 'Hacen falta las indicaciones para la IA'
                  }
                >
                  {subiendo ? 'Guardando…' : 'Guardar plantilla'}
                </button>
                <button style={S.boton} onClick={cerrarNueva}>Cancelar</button>
              </div>
            </div>
          ))}

          <input
            ref={inputArchivo} type="file" accept=".docx" style={{ display: 'none' }}
            onChange={(e) => {
              const archivo = e.target.files && e.target.files[0];
              // Se vacía para que volver a elegir EL MISMO archivo cuente como
              // un cambio. Sin esto, quien corrige el .docx fuera y lo vuelve a
              // elegir no consigue nada y no sabe por qué.
              e.target.value = '';
              elegirArchivo(archivo);
            }}
          />

          <div style={{ marginTop: 24 }}>
            {cargando && <div style={{ color: C.suave, fontSize: 14 }}>Cargando plantillas…</div>}

            {!cargando && formatos.length === 0 && (
              <div style={{ color: C.suave, fontSize: 14 }}>
                Todavía no hay plantillas guardadas.
              </div>
            )}

            {formatos.map(f => {
              const esPreferido = String(preferido) === String(f.formatId);
              const propio = !f.userId || f.userId === userId;

              return (
                <div key={f.formatId}>
                <div style={S.fila}>
                  {/* Marcarla es elegirla. Todas las de la lista llevan la
                      misma marca, así que cuál está puesta se ve sin leer. */}
                  <label style={S.marca} title="Usar esta plantilla por defecto">
                    <input
                      type="radio"
                      name="plantilla-preferida"
                      style={S.radio}
                      checked={esPreferido}
                      onChange={() => elegirPreferido(f.formatId)}
                    />
                    {esPreferido ? 'Preferida' : ''}
                  </label>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.nombre}>
                      {f.name}
                      {f.isPublic && <span style={S.insignia(C.exito)}>compartido</span>}
                      {!propio && <span style={S.insignia(C.tenue)}>de otra persona</span>}
                    </div>
                    <div style={S.detalle}>
                      {f.fileName}
                      {f.stats && ` · ${f.stats.sectionCount} secciones`}
                      {f.stats && f.stats.placeholderCount > 0
                        ? ` · ${f.stats.placeholderCount} campos marcados`
                        : f.stats && f.stats.tableCount > 0
                          ? ` · ${f.stats.tableCount} tablas (se rellenan por su estructura)`
                          : ''}
                    </div>
                  </div>

                  {propio && (
                    <>
                      <button
                        style={S.boton}
                        onClick={() => (editando === f.formatId ? setEditando(null) : abrirEdicion(f))}
                      >
                        {editando === f.formatId ? 'Cerrar' : 'Editar'}
                      </button>
                      <button style={S.boton} onClick={() => cambiarVisibilidad(f)}>
                        {f.isPublic ? 'Hacer privado' : 'Compartir'}
                      </button>
                      <button style={{ ...S.boton, ...S.botonPeligro }} onClick={() => borrar(f)}>
                        Borrar
                      </button>
                    </>
                  )}
                </div>

                {editando === f.formatId && (
                  <div style={{
                    border: `1px solid ${C.borde}`, borderRadius: 10,
                    padding: 16, marginBottom: 10, background: C.panel
                  }}>
                  {/* La plantilla, delante y escribible.

                      Dos cosas a la vez, y las dos hacían falta. Verla, porque
                      las indicaciones se escriben PARA este documento —«el
                      resumen va en la tabla de arriba», «los acuerdos en la
                      segunda columna»— y escribirlas de memoria es de donde
                      salen las instrucciones que no encajan con el acta real.
                      Y poder tocarla, porque hasta ahora corregir una errata del
                      encabezado obligaba a bajar el .docx, abrirlo, arreglarlo y
                      volver a subirlo como plantilla nueva.

                      Es el mismo recuadro que edita las actas. No por ahorrar:
                      ese sabe esperar a que Word termine de guardar antes de
                      traerse el archivo, y hacer otro habría sido reaprender eso
                      a base de plantillas a medio guardar.

                      A su OneDrive y no a la biblioteca del equipo: la
                      biblioteca es donde se buscan actas, y llenarla de machotes
                      la estropea. */}
                  <p style={{ fontSize: 12.5, color: C.tenue, margin: '0 0 8px', lineHeight: 1.55 }}>
                    {guardandoDocx
                      ? 'Guardando el documento en la plantilla…'
                      : 'Escribe aquí para corregir la plantilla. Lo que guardes en Word se guarda en ella.'}
                  </p>

                  {docxPlantilla ? (
                    <VistaWord
                      base64={docxPlantilla}
                      titulo={`Plantilla - ${f.name}`}
                      tokenGraph={tokenGraph}
                      enBiblioteca={false}
                      leerContenido={false}
                      onCambios={({ base64 }) => guardarDocxPlantilla(f, base64)}
                      activa
                    />
                  ) : (
                    <p style={{ fontSize: 12.5, color: C.tenue, margin: '0 0 8px' }}>
                      Abriendo la plantilla en Word…
                    </p>
                  )}

                    <label style={{ ...S.etiqueta, marginTop: 18 }}>
                      Cómo debe redactar el acta con "{f.name}"
                    </label>
                    <textarea
                      style={S.area}
                      value={promptEditado}
                      placeholder={EJEMPLO_PROMPT}
                      onChange={(e) => setPromptEditado(e.target.value)}
                    />
                    <div style={S.contador}>
                      {promptEditado.trim().length < MINIMO_PROMPT
                        ? `Al menos ${MINIMO_PROMPT} caracteres (llevas ${promptEditado.trim().length}).`
                        : `${promptEditado.trim().length} caracteres`}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        style={{
                          ...S.boton, ...S.botonAcento,
                          opacity: promptEditado.trim().length >= MINIMO_PROMPT ? 1 : .5
                        }}
                        onClick={guardarPrompt}
                        disabled={guardandoPrompt || promptEditado.trim().length < MINIMO_PROMPT}
                      >
                        {guardandoPrompt ? 'Guardando…' : 'Guardar indicaciones'}
                      </button>
                      <button style={S.boton} onClick={() => setEditando(null)}>Cancelar</button>
                    </div>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* ── Permisos ── */}
        {apartado === 'permisos' && puedo && puedo.administra && (
        <section style={S.seccion}>
          <h2 style={S.h2}>Quién puede subir plantillas</h2>
          <p style={S.ayuda}>
            Una plantilla no es de quien la sube: lo que diga sale en las actas de toda
            la organización. Por eso subirlas y compartirlas lo lleva este grupo.
            El resto las usa para generar sus actas, como siempre.
          </p>

          {avisoPermisos && (
            <div style={aviso(avisoPermisos.malo ? 'error' : 'exito')}>{avisoPermisos.texto}</div>
          )}

          <div style={{ maxWidth: 460, marginBottom: 18 }}>
            <label style={S.etiqueta}>Añadir personas</label>

            {/* Las elegidas, como fichas. Se eligen varias y se guardan de una
                vez: dar permiso a un equipo de cinco no son cinco visitas. */}
            {porAñadir.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {porAñadir.map(p => (
                  <span
                    key={p.id}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 6px 3px 10px', borderRadius: 999, fontSize: 12.5,
                      background: C.panelAlto, border: `1px solid ${C.borde}`
                    }}
                  >
                    {p.nombre || p.correo}
                    <button
                      style={{
                        border: 0, background: 'transparent', color: C.tenue,
                        cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px'
                      }}
                      title={`Quitar a ${p.nombre || p.correo} de la selección`}
                      onClick={() => quitarDeElegidas(p.id)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <SelectorDePersona
              // Quien ya tiene permiso no se ofrece. Elegirlo otra vez no lo
              // añadiria: le BAJARIA el papel al de por omision, y asi es como
              // un administrador se quito a si mismo sin querer.
              personas={directorio.filter(p => {
                const suId = identidadDe(p);
                if ((permisos || []).some(x => x.userId === suId)) return false;
                return !porAñadir.some(x => x.id === p.id);
              })}
              valor=""
              textoMostrado=""
              vacio={porAñadir.length > 0 ? '+ Añadir a otra persona' : '+ Buscar en el directorio'}
              etiqueta="Elegir a alguien del directorio"
              onCambio={(id, persona) => elegirParaPermiso(persona)}
            />

            {porAñadir.length > 0 && (
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button
                  style={{ ...S.boton, ...S.botonAcento }}
                  onClick={guardarElegidas}
                  disabled={dando}
                >
                  {dando
                    ? 'Dando permiso…'
                    : porAñadir.length === 1
                      ? 'Dar permiso a 1 persona'
                      : `Dar permiso a ${porAñadir.length} personas`}
                </button>
                <button style={S.boton} onClick={() => setPorAñadir([])} disabled={dando}>
                  Cancelar
                </button>
              </div>
            )}

            <p style={{ ...S.ayuda, marginTop: 8 }}>
              Entran con «Plantillas». Para que además puedan repartir permisos, cámbiales
              el papel en la lista de abajo. Quien ya está no aparece aquí.
            </p>
          </div>

          {permisos === null ? (
            <p style={S.ayuda}>Leyendo la lista…</p>
          ) : falloAlLeer ? (
            // Ni «no hay nadie» ni la lista: no se sabe. Decir «todavia no hay
            // nadie» sobre una lista que no se pudo leer es tranquilizar con
            // algo que no se sabe — y es lo que se vio la primera vez que esto
            // fallo, con un error en rojo justo encima.
            <p style={S.ayuda}>No se pudo leer la lista, así que no se sabe quién tiene permiso.</p>
          ) : permisos.length === 0 ? (
            <p style={S.ayuda}>Todavía no hay nadie. Nadie podrá subir plantillas.</p>
          ) : (
            <div style={{ border: `1px solid ${C.borde}`, borderRadius: 10, overflow: 'hidden' }}>
              {permisos.map(p => {
                const soyYo = p.userId === userId;
                return (
                  <div
                    key={p.userId}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '11px 14px', borderBottom: `1px solid ${C.borde}`
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13.5 }}>
                        {p.nombre || p.correo || p.userId}
                        {soyYo && <span style={{ color: C.tenue, fontSize: 12 }}> · tú</span>}
                      </span>
                      <br />
                      <span style={{ fontSize: 11.5, color: C.tenue }}>
                        {p.correo || p.userId}
                        {p.creadoPor && p.creadoPor !== 'migracion' && ` · lo dio ${p.creadoPor}`}
                      </span>
                    </span>

                    {/* El papel se cambia aqui mismo. Quitar y volver a dar es
                        un paso en el que uno se puede quedar sin nadie. */}
                    <select
                      style={S.combo || { padding: '5px 8px', fontFamily: FUENTE, fontSize: 12.5 }}
                      value={p.permiso}
                      aria-label={`Papel de ${p.nombre || p.correo}`}
                      onChange={(e) => cambiarPermiso(p, e.target.value)}
                    >
                      <option value="plantillas">Plantillas</option>
                      <option value="admin">Plantillas y permisos</option>
                    </select>

                    <button
                      style={{ ...S.boton, padding: '5px 11px', fontSize: 12.5 }}
                      onClick={() => sacarDePermisos(p)}
                      title={`Quitarle el permiso a ${p.nombre || p.correo}`}
                    >
                      Quitar
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Lo que impide cerrar por fuera con las llaves dentro. El servidor
              tambien lo comprueba —es el que manda—, pero decirlo aqui evita
              el intento y la explicacion a posteriori. */}
          <p style={{ ...S.ayuda, marginTop: 12 }}>
            Tiene que quedar siempre al menos una persona con «Plantillas y permisos».
            Si no, nadie podría volver a tocar esta lista sin entrar a la base de datos.
          </p>
        </section>
        )}

        {/* ── Correo ── */}
        {apartado === 'correo' && (
        <section style={S.seccion}>
          <h2 style={S.h2}>Correo</h2>
          <p style={S.ayuda}>
            Desde aquí salen las solicitudes de firma, los avisos de rechazo y las
            confirmaciones. Hace falta un buzón de salida: la aplicación se conecta a él
            por SMTP con su usuario y contraseña.
          </p>

          {!hayClaveCorreo && (
            <div style={{ ...S.ayuda, color: C.error }}>
              Falta el ajuste <code>CORREO_CLAVE</code> en el servidor. Sin él no se puede
              guardar la contraseña del buzón, porque se guarda cifrada.
            </div>
          )}

          {perfiles === null && <p style={S.ayuda}>Cargando…</p>}

          {perfiles !== null && perfiles.length === 0 && !buzon && (
            <p style={S.ayuda}>Todavía no hay ningún buzón configurado.</p>
          )}

          {(perfiles || []).map(p => (
            <div key={p.id} style={S.tarjetaCorreo}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>{p.nombre}</strong>
                {p.esPrincipal && <span style={S.selloBuzon}>principal</span>}
                {!p.activo && <span style={{ ...S.selloBuzon, background: C.borde }}>apagado</span>}
              </div>
              <div style={{ fontSize: 12.5, color: C.suave, marginTop: 4, lineHeight: 1.6 }}>
                {p.host}:{p.puerto}{p.usaTLS ? ' · TLS' : ''}<br />
                Envía como {p.desdeNombre ? `${p.desdeNombre} <${p.desdeCorreo}>` : p.desdeCorreo}<br />
                {/* Nunca la contraseña: solo si la hay. El servidor no la devuelve. */}
                {p.tienePassword ? 'Con contraseña guardada' : 'Sin contraseña'}
                {p.usuario ? ` · usuario ${p.usuario}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 7, marginTop: 9, flexWrap: 'wrap' }}>
                <button
                  style={S.boton}
                  onClick={() => probarCorreo(p.id)}
                  disabled={probando === p.id}
                >
                  {probando === p.id ? 'Enviando…' : 'Enviar correo de prueba'}
                </button>
                <button
                  style={S.boton}
                  onClick={() => { setBuzon({ ...p, password: '' }); setAvisoCorreo(null); }}
                >
                  Editar
                </button>
                {!p.esPrincipal && (
                  <button
                    style={S.boton}
                    onClick={async () => { await marcarPerfilPrincipal(userId, p.id); refrescarCorreo(); }}
                  >
                    Usar este
                  </button>
                )}
                <button
                  style={S.boton}
                  onClick={async () => {
                    if (!await confirmar({
                      titulo: 'Quitar este buzón',
                      cuerpo: `Se borrará «${p.nombre}» y su contraseña. Si era el principal, los correos dejarán de salir hasta que configures otro.`,
                      confirmar: 'Quitarlo'
                    })) return;
                    await borrarPerfilDeCorreo(userId, p.id);
                    refrescarCorreo();
                  }}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}

          {!buzon && (
            <button
              style={{ ...S.boton, marginTop: 10 }}
              onClick={() => { setBuzon({ ...NUEVO }); setAvisoCorreo(null); }}
            >
              + Añadir un buzón
            </button>
          )}

          {buzon && (
            <div style={{ ...S.tarjetaCorreo, marginTop: 12 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14.5, fontWeight: 600 }}>
                {buzon.id ? 'Editar el buzón' : 'Un buzón nuevo'}
              </h3>

              <div style={S.rejillaCorreo}>
                <div>
                  <label style={S.etiqueta}>Cómo lo llamas</label>
                  <input style={S.campo} value={buzon.nombre}
                    placeholder="Buzón de minutas"
                    onChange={e => setBuzon(v => ({ ...v, nombre: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>Servidor SMTP</label>
                  <input style={S.campo} value={buzon.host}
                    placeholder="smtp.office365.com"
                    onChange={e => setBuzon(v => ({ ...v, host: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>Puerto</label>
                  <input style={S.campo} type="number" value={buzon.puerto}
                    onChange={e => setBuzon(v => ({ ...v, puerto: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>Usuario</label>
                  <input style={S.campo} value={buzon.usuario} autoComplete="off"
                    placeholder="minutas@empresa.com"
                    onChange={e => setBuzon(v => ({ ...v, usuario: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>
                    Contraseña
                    {buzon.id && buzon.tienePassword ? ' (hay una guardada)' : ''}
                  </label>
                  {/* Nunca se rellena con la guardada: el servidor no la
                      devuelve. Vacío significa «déjala como está». */}
                  <input style={S.campo} type="password" value={buzon.password}
                    autoComplete="new-password"
                    placeholder={buzon.id && buzon.tienePassword
                      ? 'Escríbela solo si quieres cambiarla' : ''}
                    onChange={e => setBuzon(v => ({ ...v, password: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>Se envía desde</label>
                  <input style={S.campo} value={buzon.desdeCorreo}
                    placeholder="minutas@empresa.com"
                    onChange={e => setBuzon(v => ({ ...v, desdeCorreo: e.target.value }))} />
                </div>
                <div>
                  <label style={S.etiqueta}>Con el nombre</label>
                  <input style={S.campo} value={buzon.desdeNombre}
                    placeholder="Minutas"
                    onChange={e => setBuzon(v => ({ ...v, desdeNombre: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 22 }}>
                  <input id="usa-tls" type="checkbox" checked={buzon.usaTLS}
                    onChange={e => setBuzon(v => ({ ...v, usaTLS: e.target.checked }))} />
                  <label htmlFor="usa-tls" style={{ ...S.etiqueta, margin: 0 }}>
                    Usar TLS (STARTTLS)
                  </label>
                </div>
              </div>

              <p style={{ ...S.ayuda, margin: '12px 0 10px', fontSize: 12.5 }}>
                El puerto 465 habla cifrado desde el principio y no usa esa casilla; el 587
                empieza en claro y sube a TLS con ella marcada, que es lo normal.
                <br />
                Si el buzón es de Microsoft 365, ten en cuenta que SMTP AUTH viene
                desactivado de fábrica: hay que habilitarlo para esa cuenta. El correo de
                prueba te lo dirá enseguida.
              </p>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  style={{ ...S.boton, ...S.botonAcento }}
                  onClick={guardarCorreo}
                  disabled={guardandoCorreo || !buzon.nombre.trim()
                    || !buzon.host.trim() || !buzon.desdeCorreo.trim()}
                >
                  {guardandoCorreo ? 'Guardando…' : 'Guardar'}
                </button>
                <button style={S.boton} onClick={() => { setBuzon(null); setAvisoCorreo(null); }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {avisoCorreo && (
            <p style={{
              marginTop: 12, fontSize: 12.5, lineHeight: 1.6,
              color: avisoCorreo.malo ? C.error : C.acentoClaro
            }}>
              {avisoCorreo.texto}
            </p>
          )}
        </section>
        )}

        {apartado === 'apariencia' && (
        <section style={S.seccion}>
          <h2 style={S.h2}>Apariencia</h2>
          <p style={S.ayuda}>
            La elección es tuya y te acompaña a cualquier equipo donde inicies sesión.
          </p>
          <div style={{ maxWidth: 320 }}>
            <SelectorTema />
          </div>
        </section>
        )}

        {/* ── Sesión ── */}
        {apartado === 'sesion' && (
        <section style={S.seccion}>
          <h2 style={S.h2}>Sesión</h2>
          <p style={S.ayuda}>
            Al cerrarla, la app deja de tener acceso a tu calendario y a tus grabaciones en este
            navegador. Tus plantillas y minutas guardadas siguen donde están.
          </p>
          <Cuenta />
        </section>
        )}

        {/* ── Estado ── */}
        {apartado === 'estado' && motor && (
          <section style={S.seccion}>
            <h2 style={S.h2}>Estado del sistema</h2>
            <div style={{ fontSize: 13.5, lineHeight: 2, color: C.suave }}>
              <div>
                Motor de IA:{' '}
                <span style={{ color: motor.ready ? C.exito : C.error }}>
                  {motor.ready ? `listo (${motor.deployment})` : 'no configurado'}
                </span>
              </div>
              <div>
                Base de datos:{' '}
                <span style={{ color: motor.bd && motor.bd.ready ? C.exito : C.error }}>
                  {motor.bd && motor.bd.ready ? 'conectada' : 'sin conexión'}
                </span>
              </div>
              {motor.missingConfig && motor.missingConfig.length > 0 && (
                <div>Falta configurar: {motor.missingConfig.join(', ')}</div>
              )}
            </div>
          </section>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuracion;
